import React, { useState, useEffect } from 'react';
import { getAnalyticsSummary, exportAnalyticsData, clearAnalyticsData } from '../utils/analyticsUtils';
import { 
  getBlogPosts, 
  addBlogPost, 
  deleteBlogPost, 
  exportBlogPosts 
} from '../utils/blogManagementUtils';
import './Dashboard.css';

const DASHBOARD_PASSWORD = 'AadhaarKoul2024!'; // Change this to your desired password

const Dashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');
  const [showBlogManager, setShowBlogManager] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPost, setNewPost] = useState({
    title: '',
    text: '',
    linkUrl: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [loadingPosts, setLoadingPosts] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      setSummary(getAnalyticsSummary());
      fetchBlogPosts();
    }
  }, [isAuthenticated]);

  const fetchBlogPosts = async () => {
    setLoadingPosts(true);
    const posts = await getBlogPosts();
    setBlogPosts(posts);
    setLoadingPosts(false);
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === DASHBOARD_PASSWORD) {
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Incorrect password.');
    }
  };

  const handleExport = () => {
    exportAnalyticsData();
  };

  const handleClear = () => {
    if (window.confirm('Are you sure you want to clear all analytics data?')) {
      clearAnalyticsData();
      setSummary(getAnalyticsSummary());
    }
  };

  const handleAddPost = async () => {
    if (!newPost.title || !newPost.text || !newPost.linkUrl) {
      alert('Please fill in all fields');
      return;
    }
    const result = await addBlogPost(newPost);
    if (result.success) {
      setNewPost({
        title: '',
        text: '',
        linkUrl: '',
        date: new Date().toISOString().split('T')[0]
      });
      setShowAddForm(false);
      await fetchBlogPosts();
      alert('Blog post added successfully!');
    } else {
      alert(`Error adding blog post: ${result.error}`);
    }
  };

  const handleDeletePost = async (postId) => {
    if (window.confirm('Are you sure you want to delete this blog post?')) {
      const result = await deleteBlogPost(postId);
      if (result.success) {
        await fetchBlogPosts();
        alert('Blog post deleted successfully!');
      } else {
        alert(`Error deleting blog post: ${result.error}`);
      }
    }
  };

  const handleExportBlogPosts = () => {
    if (exportBlogPosts()) {
      alert('Blog posts exported successfully!');
    } else {
      alert('Error exporting blog posts');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="dashboard-login">
        <h2>Admin Dashboard Login</h2>
        <form onSubmit={handleLogin}>
          <input
            type="password"
            placeholder="Enter dashboard password"
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
          <button type="submit">Login</button>
        </form>
        {error && <div className="error">{error}</div>}
      </div>
    );
  }

  if (!summary) return <div className="dashboard-loading">Loading analytics...</div>;

  return (
    <div className="dashboard-container">
      <h1>Portfolio Analytics Dashboard</h1>
      
      <div className="dashboard-metrics">
        <div className="dashboard-metric-card">
          <h3>Total Page Views</h3>
          <div className="metric-value">{summary.totalPageViews}</div>
          <div className="metric-subtitle">Today: {summary.todayPageViews}</div>
        </div>
        
        <div className="dashboard-metric-card">
          <h3>Total Button Clicks</h3>
          <div className="metric-value">{summary.totalButtonClicks}</div>
          <div className="metric-subtitle">Today: {summary.todayButtonClicks}</div>
        </div>
        
        <div className="dashboard-metric-card">
          <h3>Blog Views</h3>
          <div className="metric-value">{summary.totalBlogViews}</div>
          <div className="metric-subtitle">Total blog post views</div>
        </div>
        
        <div className="dashboard-metric-card">
          <h3>Blog Likes</h3>
          <div className="metric-value">{summary.totalBlogLikes}</div>
          <div className="metric-subtitle">Total blog post likes</div>
        </div>
      </div>

      <div className="dashboard-section">
        <h2>Popular Pages</h2>
        <ul>
          {summary.popularPages.map(page => (
            <li key={page.page}>
              <strong>{page.page}</strong>: {page.totalViews} views
            </li>
          ))}
        </ul>
      </div>

      <div className="dashboard-section">
        <h2>Most Clicked Buttons</h2>
        <ul>
          {summary.popularButtons.map(btn => (
            <li key={btn.button}>
              <strong>{btn.button}</strong>: {btn.totalClicks} clicks
            </li>
          ))}
        </ul>
      </div>

      <div className="dashboard-section">
        <h2>Popular Blog Posts</h2>
        <ul>
          {summary.popularPosts.map(post => (
            <li key={post.id}>
              <strong>{post.title || post.id}</strong>: {post.views} views, {post.likes} likes
            </li>
          ))}
        </ul>
      </div>

      {/* Blog Management Section */}
      <div className="dashboard-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2>Blog Management</h2>
          <button 
            onClick={() => setShowBlogManager(!showBlogManager)}
            className="dashboard-toggle-btn"
          >
            {showBlogManager ? 'Hide' : 'Show'} Blog Manager
          </button>
        </div>
        
        {showBlogManager && (
          <div className="blog-manager">
            <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
              <button 
                onClick={() => setShowAddForm(!showAddForm)}
                className="dashboard-action-btn"
              >
                {showAddForm ? 'Cancel' : 'Add New Post'}
              </button>
              <button 
                onClick={handleExportBlogPosts}
                className="dashboard-action-btn"
              >
                Export Blog Posts
              </button>
            </div>

            {showAddForm && (
              <div className="add-post-form">
                <h3>Add New Blog Post</h3>
                <div className="form-group">
                  <label>Title:</label>
                  <input
                    type="text"
                    value={newPost.title}
                    onChange={(e) => setNewPost({...newPost, title: e.target.value})}
                    placeholder="Enter post title"
                  />
                </div>
                <div className="form-group">
                  <label>Text:</label>
                  <textarea
                    value={newPost.text}
                    onChange={(e) => setNewPost({...newPost, text: e.target.value})}
                    placeholder="Enter post content"
                    rows="4"
                  />
                </div>
                <div className="form-group">
                  <label>Link URL:</label>
                  <input
                    type="url"
                    value={newPost.linkUrl}
                    onChange={(e) => setNewPost({...newPost, linkUrl: e.target.value})}
                    placeholder="Enter link URL"
                  />
                </div>
                <div className="form-group">
                  <label>Date:</label>
                  <input
                    type="date"
                    value={newPost.date}
                    onChange={(e) => setNewPost({...newPost, date: e.target.value})}
                  />
                </div>
                <button onClick={handleAddPost} className="dashboard-action-btn">
                  Add Post
                </button>
              </div>
            )}

            <div className="blog-posts-list">
              <h3>Current Blog Posts ({blogPosts.length})</h3>
              <div className="posts-grid">
                {loadingPosts ? (
                  <div style={{ textAlign: 'center', color: 'rgba(213, 155, 223, 0.7)', padding: '20px' }}>
                    Loading blog posts...
                  </div>
                ) : blogPosts.length === 0 ? (
                  <div style={{ textAlign: 'center', color: 'rgba(213, 155, 223, 0.7)', padding: '20px' }}>
                    No blog posts found. Add your first post!
                  </div>
                ) : (
                  blogPosts.map(post => (
                    <div key={post.id} className="post-item">
                      <div className="post-header">
                        <h4>{post.title}</h4>
                        <span className="post-date">{post.date}</span>
                      </div>
                      <p className="post-text">{post.text.substring(0, 100)}...</p>
                      <div className="post-stats">
                        <span>👁 {post.views}</span>
                        <span>❤ {post.likes}</span>
                      </div>
                      <button 
                        onClick={() => handleDeletePost(post.id)}
                        className="delete-btn"
                      >
                        Delete
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="dashboard-actions">
        <button onClick={handleExport}>Export Analytics</button>
        <button onClick={handleClear} className="danger">Clear Analytics</button>
      </div>
      
      <div className="dashboard-last-updated">
        Last updated: {summary.lastUpdated && new Date(summary.lastUpdated).toLocaleString()}
      </div>
    </div>
  );
};

export default Dashboard; 